import RolePopupManager from './RolePopupManager.js';
import { MarqueeSelect } from './MarqueeSelect.js'; 

export class SandboxView {
    constructor(config) {
        this.containerId = config.container;
        this.data = config.data ? JSON.parse(JSON.stringify(config.data)) : [];
        this.rootId = config.rootId;
        this.orgId = config.orgId;
        this.currentUser = config.currentUser || {};
        this.canApprove = config.canApprove !== undefined ? config.canApprove : true;
        
        this.apiUrl = config.pythonApiUrl || ''; 
        if (this.apiUrl && this.apiUrl.endsWith('/')) {
            this.apiUrl = this.apiUrl.slice(0, -1);
        }
        this.actions = config.actions || {};
        this.displaySettings = config.displaySettings || { cc: '#F59E0B', tc: '#fff', bc: '#e5e7eb' };
        
        this.cardWidth = 340; 
        this.cardHeight = 180; 
        this.gapX = 80;
        this.gapY = 160;
        
        this.zoom = 0.6;
        this.panX = 0;
        this.panY = 0;
        this.isBackgroundDragging = false;
        this.marquee = null;
        this.activeTool = 'pan';
        this.resizeObserver = null; 
        
        this.pendingJoins = [];
        this.pollInterval = null;
        this.viewMode = 'hierarchy'; // 'hierarchy' | 'departments'
        this.activeDepartmentId = null;
        
        this.init();
    }

    init() {
        this.injectStyles();
        this.renderLayout();
        
        window.addEventListener('kvdr-sandbox-refresh', (e) => this.handleRefresh(e));

        this.refreshData();
        this.setupSearch(); 
        
        this.attachEvents();
        this.setupNotifications();
        
        this.marquee = new MarqueeSelect({
            wrapper: this.wrapper,
            getNodes: () => Object.values(this.nodeLookup || {}).map(node => ({
                id: node.selfId,
                data: node
            })),
            onSelectionChange: (selectedIds) => this._updateNodeSelectionStyles(selectedIds),
            
            onSelectionComplete: async (selectedNodes) => {
                // Dispatch event so RoleDesign.vue can open the Inspector
                window.dispatchEvent(new CustomEvent('show-role-inspector', { 
                    detail: { nodes: selectedNodes.map(n => n.data) } 
                }));
            }
        });
    }

    switchViewMode(mode) {
        if (this.viewMode === mode) return;
        this.viewMode = mode;
        this.activeDepartmentId = null; // Reset drill-down
        
        document.getElementById('sb-mode-hierarchy').classList.toggle('active', mode === 'hierarchy');
        document.getElementById('sb-mode-departments').classList.toggle('active', mode === 'departments');
        
        this.refreshData();
        this.resetView(); // Re-center map smoothly
    }

    // --- NEW: Search Initialization Logic ---
    setupSearch() {
        const searchInput = this.wrapper.querySelector('#sb-search-input');
        const searchResults = this.wrapper.querySelector('#sb-search-results');
        
        if (!searchInput || !searchResults) return;

        searchInput.addEventListener('input', (e) => {
            const query = e.target.value.toLowerCase().trim();
            searchResults.innerHTML = '';
            
            if (query.length < 2) {
                searchResults.classList.remove('active');
                return;
            }

            let results = [];

            // 1. Search roles & candidates dynamically from the current data tree
            (this.data || []).forEach(role => {
                // Check Role Title
                if (role.title && role.title.toLowerCase().includes(query)) {
                    results.push({
                        type: 'role',
                        id: role.selfId,
                        title: role.title,
                        sub: role.isRoot ? 'Head of Organization' : 'Role',
                        image: role.image || role.logoUrl || null
                    });
                }
                
                // Check Candidates assigned to the role
                const profiles = role.profiles || role.kvdrRoleProfiles || [];
                profiles.forEach(p => {
                    const ud = p.userData || {};
                    const name = p.name || p.fullName || p.firstName || ud.name || ud.firstName || '';
                    const email = p.email || ud.email || '';
                    
                    if (name.toLowerCase().includes(query) || email.toLowerCase().includes(query)) {
                        const picUrl = ud.profilePictureUrl || p.profilePictureUrl || ud.profilePicture || p.profilePicture || p.avatar;
                        results.push({
                            type: 'candidate',
                            id: role.selfId, // Target the role node to center on it
                            title: name,
                            sub: `in ${role.title}`,
                            image: picUrl
                        });
                    }
                });
            });

            // 2. Render Results
            if (results.length > 0) {
                // Limit to 10 results so UI doesn't lag
                results.slice(0, 10).forEach(res => {
                    const item = document.createElement('div');
                    item.className = 'sb-search-item';
                    
                    let avatarHtml = '';
                    
                    if (res.type === 'role' && (!res.image || res.image === 'null')) {
                        // Briefcase Icon for Roles (Matches Evaluation Page)
                        avatarHtml = `
                            <div class="sb-search-avatar" style="display:flex; align-items:center; justify-content:center; background:#f8fafc; color:#94a3b8; border: 1px solid #e2e8f0; border-radius: 8px;">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                                </svg>
                            </div>`;
                    } else {
                        // Image Avatar for Candidates (or roles that have logos)
                        const finalName = res.title || "User";
                        let imgSrc = res.image && res.image !== 'null' ? 
                            (res.image.startsWith('http') ? res.image : `https://gtbrdd-storage.s3.us-west-2.amazonaws.com/editor/profile-pictures/${res.image}`) : 
                            `https://hostedboringavatars.vercel.app/api/beam?name=${encodeURIComponent(finalName)}`;
                        avatarHtml = `<img class="sb-search-avatar" src="${imgSrc}" style="border-radius: 50%;" onerror="this.src='https://hostedboringavatars.vercel.app/api/beam?name=Error'" />`;
                    }

                    item.innerHTML = `
                        ${avatarHtml}
                        <div class="sb-search-details">
                            <span class="sb-search-name">${res.title}</span>
                            <span class="sb-search-sub">${res.sub}</span>
                        </div>
                    `;
                    
                    item.addEventListener('click', () => {
                        this.highlightAndCenterNode(res.id);
                        searchResults.classList.remove('active');
                        searchInput.value = '';
                    });
                    
                    searchResults.appendChild(item);
                });
                searchResults.classList.add('active');
            } else {
                searchResults.innerHTML = `<div style="padding: 12px; text-align: center; color: #64748b; font-size: 13px;">No results found</div>`;
                searchResults.classList.add('active');
            }
        });

        // Hide dropdown on outside click
        document.addEventListener('click', (e) => {
            if (!searchInput.contains(e.target) && !searchResults.contains(e.target)) {
                searchResults.classList.remove('active');
            }
        });
    }

    highlightAndCenterNode(nodeId) {
        if (!this.nodeLookup || !this.nodeLookup[nodeId] || !this.wrapper || !this.canvas) return;
        
        const node = this.nodeLookup[nodeId];
        const wrapperW = this.wrapper.clientWidth;
        const wrapperH = this.wrapper.clientHeight;
        
        // Target zoom level when centering (reset closer to 1 if zoomed far out)
        this.zoom = Math.max(0.8, this.zoom); 
        
        // Calculate center coordinates
        const nodeCenterX = node.x + (this.cardWidth / 2);
        const nodeCenterY = node.y + (this.cardHeight / 2);
        
        this.panX = (wrapperW / 2) - (nodeCenterX * this.zoom);
        this.panY = (wrapperH / 2) - (nodeCenterY * this.zoom);
        
        // Apply smooth CSS transition just for the fly-to effect
        this.canvas.style.transition = "transform 0.5s cubic-bezier(0.25, 1, 0.5, 1)";
        this.updateTransform();
        
        // Remove transition after it's done so dragging doesn't lag
        clearTimeout(this.flyTimeout);
        this.flyTimeout = setTimeout(() => {
            this.canvas.style.transition = "none";
        }, 500);

        // Visual Ping/Highlight effect
        const nodeEl = this.canvas.querySelector(`.sb-node[data-id="${nodeId}"]`);
        if (nodeEl) {
            // Ensure animation style exists
            if (!document.getElementById('sb-highlight-pulse-style')) {
                const style = document.createElement('style');
                style.id = 'sb-highlight-pulse-style';
                style.innerText = `
                    @keyframes sbNodePulse {
                        0% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.7); }
                        70% { box-shadow: 0 0 0 25px rgba(245, 158, 11, 0); }
                        100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
                    }
                    .sb-pulse-active {
                        animation: sbNodePulse 1.5s cubic-bezier(0.4, 0, 0.2, 1) !important;
                        border-color: #F59E0B !important;
                        z-index: 100 !important;
                    }
                `;
                document.head.appendChild(style);
            }

            document.querySelectorAll('.sb-pulse-active').forEach(el => el.classList.remove('sb-pulse-active'));
            nodeEl.classList.add('sb-pulse-active');
            
            setTimeout(() => {
                nodeEl.classList.remove('sb-pulse-active');
            }, 1500);
        }
    }

    showRootNodePopup() {
        if (!this.treeRoot) return;
        window.dispatchEvent(new CustomEvent('show-role-details', { 
            detail: { role: this.treeRoot } 
        }));
    }

    handleRefresh(e) {
        if (e.detail && e.detail.graphData) {
            this.data = JSON.parse(JSON.stringify(e.detail.graphData));
        }
        if (e.detail && e.detail.departments) {
            this.departments = e.detail.departments; // ✨ NEW
        }
        this.refreshData();
        this.refreshNotifications();
    }

    destroy() {
        if (this.wrapper) this.wrapper.remove();
        if (this.marquee && typeof this.marquee.destroy === 'function') this.marquee.destroy();
        if (this.resizeObserver) this.resizeObserver.disconnect();
        clearTimeout(this.resizeTimeout);
        if (this.pollInterval) clearInterval(this.pollInterval);
    }

    refreshData() {
        // ✨ UPDATED: Skip normal tree layout if viewing high-level department grid
        if (this.viewMode === 'hierarchy') {
            this.calculateTreeLayout();
        } else if (this.viewMode === 'departments' && this.activeDepartmentId) {
            this.calculateDepartmentTreeLayout(); // Custom virtual tree layout
        }
        this.draw();
    }

    attachTooltip(element, text) {
        if(!element) return;
        element.removeAttribute("title");

        if (!document.getElementById('sandbox-custom-tooltip-style')) {
            const css = `
                .sandbox-custom-tooltip {
                    position: fixed; background: #1f1f1f; color: #fff; padding: 6px 10px;
                    border-radius: 6px; font-size: 12px; font-family: inherit;
                    white-space: nowrap; z-index: 1000000; pointer-events: none;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.2); opacity: 0;
                    animation: sbTooltipFadeIn 0.2s forwards; transform: translateX(-50%); margin-top: -8px;
                }
                .sandbox-custom-tooltip::after {
                    content: ''; position: absolute; top: 100%; left: 50%;
                    margin-left: -5px; border-width: 5px; border-style: solid;
                    border-color: #1f1f1f transparent transparent transparent;
                }
                @keyframes sbTooltipFadeIn { 
                    from { opacity: 0; transform: translateX(-50%) translateY(5px); } 
                    to { opacity: 1; transform: translateX(-50%) translateY(0); } 
                }
            `;
            const style = document.createElement('style');
            style.id = 'sandbox-custom-tooltip-style';
            style.innerHTML = css;
            document.head.appendChild(style);
        }

        element.addEventListener('mouseenter', () => {
            const tip = document.createElement('div');
            tip.className = 'sandbox-custom-tooltip';
            tip.innerText = text;
            document.body.appendChild(tip);
            const rect = element.getBoundingClientRect();
            tip.style.top = (rect.top - tip.offsetHeight) + 'px';
            tip.style.left = (rect.left + (rect.width / 2)) + 'px';
        });

        element.addEventListener('mouseleave', () => document.querySelectorAll('.sandbox-custom-tooltip').forEach(el => el.remove()));
        element.addEventListener('click', () => document.querySelectorAll('.sandbox-custom-tooltip').forEach(el => el.remove()));
    }

    injectStyles() {
        const oldStyle = document.getElementById('sb-styles-ui-v2');
        if (oldStyle) oldStyle.remove();

        const css = `
            .sb-wrapper {
                position: absolute !important; top: 0; left: 0; width: 100%; height: 100%;
                background-color: #f8f9fa; 
                font-family: 'Inter', sans-serif;
                overflow: hidden !important; display: block; border-radius: 8px; 
                border: 1px solid #e5e7eb; box-sizing: border-box;
            }
            .sb-canvas { transform-origin: 0 0; position: absolute; top: 0; left: 0; width: 100%; height: 100%; }
            .sb-svg-layer { position: absolute; top: 0; left: 0; pointer-events: none; overflow: visible; z-index: 0; }
            .sb-connector { fill: none; stroke: #94a3b8; stroke-width: 2px; }

            .sb-controls {
                position: absolute; top: 0; left: 0; right: 0; height: 56px;
                display: flex; flex-direction: row; align-items: center; justify-content: space-between;
                background: white; border-bottom: 1px solid #e5e7eb; padding: 0 16px; box-sizing: border-box; z-index: 10000;
                border-top-left-radius: 8px; border-top-right-radius: 8px;
            }
            
            .sb-ctrl-left { display: flex; align-items: center; flex: 1; }
            .sb-search-wrapper { position: relative; width: 340px; }
            .sb-search-input-box {
                width: 100%; padding: 8px 16px 8px 38px; border: 1px solid #e2e8f0;
                border-radius: 20px; font-size: 13px; outline: none; transition: all 0.2s;
                box-sizing: border-box; background: #f8fafc; color: #1e293b;
            }
            .sb-search-input-box::placeholder { color: #94a3b8; }
            .sb-search-input-box:focus { border-color: var(--sb-primary); background: #fff; box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.15); }
            .sb-search-icon {
                position: absolute; left: 14px; top: 50%; transform: translateY(-50%);
                color: #94a3b8; width: 16px; height: 16px; pointer-events: none;
            }
            .sb-search-dropdown {
                position: absolute; top: calc(100% + 8px); left: 0; width: 100%;
                background: #fff; border: 1px solid #e2e8f0; border-radius: 8px;
                box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1); max-height: 320px; overflow-y: auto;
                z-index: 10001; display: none; flex-direction: column; padding: 6px; box-sizing: border-box;
            }
            .sb-search-dropdown.active { display: flex; }
            .sb-search-item {
                display: flex; align-items: center; gap: 12px; padding: 8px 10px; cursor: pointer;
                border-radius: 6px; transition: background 0.15s;
            }
            .sb-search-item:hover { background: #f1f5f9; }
            .sb-search-avatar { width: 32px; height: 32px; border-radius: 50%; object-fit: cover; background: #e2e8f0; flex-shrink: 0; }
            .sb-search-details { display: flex; flex-direction: column; overflow: hidden; }
            .sb-search-name { font-size: 13px; font-weight: 600; color: #0f172a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
            .sb-search-sub { font-size: 11px; color: #64748b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 2px;}

            .sb-ctrl-right { display: flex; align-items: center; gap: 8px; }
            .sb-ctrl-btn {
                width: 34px; height: 34px; border: none; background: transparent;
                display: flex; align-items: center; justify-content: center;
                cursor: pointer; color: #475569; border-radius: 6px; transition: all 0.2s; position: relative;
            }
            .sb-ctrl-btn:hover { background: #f1f5f9; color: #0f172a; }
            .sb-ctrl-btn.active { background-color: #fef3c7; color: #d97706; }
            .sb-ctrl-btn svg { width: 18px; height: 18px; }

            .sb-minimap {
                position: absolute; bottom: 24px; right: 24px; width: 200px; height: 150px; background: rgba(255,255,255,0.9);
                border: 1px solid #e0e0e0; border-radius: 6px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); z-index: 10000;
                overflow: hidden; pointer-events: none;
            }
            .sb-mini-node { position: absolute; background: #cbd5e1; border-radius: 2px; }
            .sb-mini-viewport { position: absolute; border: 2px solid var(--sb-primary); background: rgba(0,0,0,0.05); box-sizing: border-box; }           
            
            .sb-node {
                position: absolute; width: ${this.cardWidth}px !important; height: ${this.cardHeight}px !important;
                background: #fff; border: 1px solid #e5e7eb; border-radius: 12px;
                box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); display: flex; flex-direction: column;
                transition: box-shadow 0.2s, transform 0.2s, border-color 0.2s, opacity 0.2s; z-index: 1; overflow: hidden;
                box-sizing: border-box; user-select: none; cursor: grab;
            }

            /* --- PENDING ROLE RESTRICTIONS --- */
            .sb-pending-role {
                opacity: 0.6;
                border: 1px dashed #eab308 !important; 
            }
            .sb-pending-role .sb-header-actions,
            .sb-pending-role .sb-add-btn {
                display: none !important;
            }
            .sb-pending-role .sb-node-body {
                pointer-events: none;
            }

            /* --- NOTIFICATION PANEL STYLES --- */
            .sb-notify-panel {
                position: absolute; top: 46px; right: 0; width: 320px; background: #fff;
                border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 10px 25px -5px rgba(0,0,0,0.15);
                z-index: 10001; max-height: 400px; overflow-y: auto; display: none;
            }
            .sb-notify-item { padding: 12px 16px; border-bottom: 1px solid #f1f5f9; cursor: pointer; transition: background 0.2s; text-align: left; }
            .sb-notify-item:hover { background: #f8fafc; }
            .sb-notify-title { font-weight: 600; font-size: 13px; color: #0f172a; margin-bottom: 4px; display:flex; justify-content:space-between; align-items:center; }
            .sb-notify-badge { font-size: 10px; padding: 2px 6px; border-radius: 12px; }
            .sb-notify-msg { font-size: 12px; color: #64748b; margin-bottom: 8px; }
            .sb-notify-actions { display: flex; gap: 8px; }
            .sb-notify-action-btn { flex: 1; padding: 6px; border: none; border-radius: 4px; font-size: 12px; font-weight: 600; cursor: pointer; }
            .sb-notify-approve { background: #dcfce7; color: #166534; }
            .sb-notify-reject { background: #fee2e2; color: #991b1b; }
            
            .sb-node:active { cursor: grabbing; }
            .sb-node.is-dragging { opacity: 0.5; border: 2px dashed #999; }
            .sb-node.drag-over-active { background-color: #e6fffa; border: 2px solid #00b894; transform: scale(1.05); z-index: 100; }
            .sb-node:hover { box-shadow: 0 20px 30px -5px rgba(0, 0, 0, 0.15); transform: translateY(-2px); z-index: 10; border-color: var(--sb-primary); }
            .sb-node.is-selected { border: 2px solid var(--sb-primary); box-shadow: 0 0 15px rgba(245, 158, 11, 0.5); }
            
            .sb-root-node-card { border: none; box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.15); display: flex; flex-direction: column; align-items: center; justify-content: center; cursor: pointer; color: #000; }
            .sb-root-content { padding: 16px 24px; display: flex; flex-direction: row; align-items: center; justify-content: space-between; width: 100%; box-sizing: border-box; gap: 16px; margin-top: auto; }
            .sb-root-title { font-size: 18px; font-weight: 600; color: #000; margin: 0; line-height: 1.3; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; text-align: left; }
            .sb-root-logo-img { width: 48px; height: 48px; border-radius: 50%; object-fit: cover; border: 2px solid #fff; box-shadow: 0 2px 6px rgba(0,0,0,0.1); background: #fff; flex-shrink: 0; }
            
            .sb-node-header { padding: 0 16px; display: flex; align-items: center; justify-content: space-between; height: 46px; flex-shrink: 0; color: #000; }
            .sb-title-span { font-weight: 700; font-size: 15px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
            .sb-header-actions { display: flex; gap: 4px; margin-left: 10px; }
            .sb-act-btn { background: rgba(255,255,255,0.25); border: none; border-radius: 4px; cursor: pointer; padding: 4px; color: white; display:flex;}
            .sb-act-btn:hover { background: rgba(255,255,255,0.5); }
            .sb-node-body { padding: 16px; font-size: 13px; color: #475569; display: flex; flex-direction: column; gap: 8px; flex: 1; overflow: hidden; cursor: pointer; }
            .sb-desc-box { width: 100%; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; font-family: 'Inter', sans-serif !important; font-size: 12px !important; color: #64748b !important; line-height: 1.5 !important; }
            .sb-desc-box * { font-family: 'Inter', sans-serif !important; font-size: 12px !important; color: #64748b !important; line-height: 1.5 !important; font-weight: normal !important; background: transparent !important; margin: 0 !important; padding: 0 !important; text-decoration: none !important; }            .sb-footer-row { display: flex; align-items: center; justify-content: space-between; margin-top: auto; padding-top: 10px; border-top: 1px solid #f3f4f6; flex-shrink: 0;}
            .sb-avatars-row { display: flex; align-items: center; }
            .sb-avatar { width: 30px; height: 30px; border-radius: 50%; border: 2px solid #fff; margin-left: -8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); object-fit: cover; background: #e2e8f0; cursor: pointer; transition: transform 0.1s; position: relative;}
            .sb-avatar:hover { transform: translateY(-3px); z-index: 50 !important; }
            .sb-avatar:first-child { margin-left: 0; }
            .sb-badge { background: #f1f5f9; padding: 4px 10px; border-radius: 20px; font-size: 11px; font-weight: 600; color: #475569; display:flex; gap:5px; align-items:center; border: 1px solid transparent; }
            
            .sb-add-btn {
                position: absolute; width: 28px; height: 28px; 
                background: #fff; border: 1px solid #cbd5e1; border-radius: 50%; 
                display: flex; align-items: center; justify-content: center;
                cursor: pointer; box-shadow: 0 4px 6px rgba(0,0,0,0.05); color: #64748b;
                z-index: 5; transition: background 0.2s, transform 0.2s; transform: translateX(-50%); 
            }
            .sb-add-btn:hover { background: var(--sb-primary); color: #fff; border-color: var(--sb-primary); transform: translateX(-50%) scale(1.15); }
            .sb-view-toggle-wrapper {
                position: absolute; left: 50%; transform: translateX(-50%);
                display: flex; background: #f1f5f9; padding: 4px; border-radius: 8px; gap: 4px;
                box-shadow: inset 0 2px 4px rgba(0,0,0,0.05); z-index: 10000;
            }
            .sb-view-mode-btn {
                border: none; background: transparent; color: #64748b; padding: 6px 14px;
                border-radius: 6px; font-size: 13px; font-weight: 600; cursor: pointer;
                display: flex; align-items: center; gap: 6px; transition: all 0.2s;
            }
            .sb-view-mode-btn:hover { color: #0f172a; }
            .sb-view-mode-btn.active {
                background: #fff; color: #F59E0B;
                box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            }

            /* ✨ NEW: Department Grid Cards */
            .sb-dept-card {
                position: absolute; width: 280px; height: 140px; background: #fff;
                border: 1px solid #e2e8f0; border-radius: 12px; display: flex; flex-direction: column;
                align-items: center; justify-content: center; text-align: center; cursor: pointer;
                box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05); transition: all 0.2s; z-index: 5;
            }
            .sb-dept-card:hover { border-color: #F59E0B; transform: translateY(-4px); box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1); }
            .sb-dept-icon { width: 40px; height: 40px; background: #fef3c7; color: #d97706; border-radius: 8px; display: flex; align-items: center; justify-content: center; margin-bottom: 12px; }
            .sb-dept-title { font-size: 15px; font-weight: 700; color: #0f172a; }
            .sb-dept-sub { font-size: 12px; color: #64748b; margin-top: 4px; }
            
            /* ✨ NEW: Back button when drilled down */
            .sb-dept-back-btn {
                position: absolute; top: 80px; left: 24px; z-index: 1000; background: #fff;
                border: 1px solid #cbd5e1; border-radius: 8px; padding: 8px 16px; font-size: 13px;
                font-weight: 600; color: #475569; cursor: pointer; box-shadow: 0 2px 4px rgba(0,0,0,0.05);
                display: flex; align-items: center; gap: 6px; transition: all 0.2s;
            }
            .sb-dept-back-btn:hover { background: #f8fafc; color: #0f172a; border-color: #94a3b8; }
        `;
        const style = document.createElement('style');
        style.id = 'sb-styles-ui-v2';
        style.innerText = css;
        document.head.appendChild(style);
    }

    renderLayout() {
        const targetContainer = document.getElementById(this.containerId);
        if(!targetContainer) return;
        targetContainer.innerHTML = ''; 

        this.wrapper = document.createElement('div');
        this.wrapper.className = 'sb-wrapper';
        this.wrapper.style.setProperty('--sb-primary', this.displaySettings.cc);

        const controls = document.createElement('div');
        controls.className = 'sb-controls';
        
        controls.innerHTML = `
            <div class="sb-ctrl-left">
                <div class="sb-search-wrapper">
                    <svg class="sb-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                    <input type="text" class="sb-search-input-box" id="sb-search-input" placeholder="Search roles or candidates..." autocomplete="off" />
                    <div class="sb-search-dropdown" id="sb-search-results"></div>
                </div>
            </div>
            <div class="sb-view-toggle-wrapper">
                <button id="sb-mode-hierarchy" class="sb-view-mode-btn active">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>
                    Hierarchy
                </button>
                <button id="sb-mode-departments" class="sb-view-mode-btn">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path></svg>
                    Departments
                </button>
            </div>
            <div class="sb-ctrl-right">
                <button class="sb-ctrl-btn" id="sb-zoom-in" title="Zoom In"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg></button>
                <button class="sb-ctrl-btn" id="sb-zoom-out" title="Zoom Out"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line></svg></button>
                
                <div style="position:relative;" id="sb-notify-wrapper">
                    <button class="sb-ctrl-btn" id="sb-notify-btn" title="Notifications">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></svg>
                        <span id="sb-notify-badge" style="display:none; position:absolute; top:2px; right:2px; background:#ef4444; color:white; border-radius:50%; font-size:9px; width:14px; height:14px; text-align:center; line-height:14px; font-weight:bold;"></span>
                    </button>
                    <div id="sb-notify-panel" class="sb-notify-panel"></div>
                </div>
                <button class="sb-ctrl-btn" id="sb-select-tool" title="Select Tool"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" stroke-dasharray="4 3"><rect x="3" y="3" width="18" height="18" rx="2"></rect></svg></button>
                
            </div>
        `;

        this.minimap = document.createElement('div');
        this.minimap.className = 'sb-minimap';

        this.canvas = document.createElement('div');
        this.canvas.className = 'sb-canvas';
        
        this.svgLayer = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        this.svgLayer.classList.add("sb-svg-layer");
        this.svgLayer.setAttribute("width", "50000"); 
        this.svgLayer.setAttribute("height", "50000");

        this.canvas.appendChild(this.svgLayer);
        this.wrapper.append(controls, this.minimap, this.canvas);
        targetContainer.appendChild(this.wrapper);
        controls.querySelector('#sb-mode-hierarchy').onclick = () => this.switchViewMode('hierarchy');
        controls.querySelector('#sb-mode-departments').onclick = () => this.switchViewMode('departments');

        controls.querySelector('#sb-zoom-in').onclick = () => this.triggerSmoothZoom(1.3);
        controls.querySelector('#sb-zoom-out').onclick = () => this.triggerSmoothZoom(1 / 1.3);
        const selectToolBtn = controls.querySelector('#sb-select-tool');
        selectToolBtn.onclick = () => {
            if (this.activeTool === 'pan') {
                this.activeTool = 'select';
                selectToolBtn.classList.add('active');
                this.wrapper.style.cursor = 'crosshair';
            } else {
                this.activeTool = 'pan';
                selectToolBtn.classList.remove('active');
                this.wrapper.style.cursor = 'grab';
                
                // Clear the selection visually when turning off the select tool
                if (this.marquee && this.marquee.selectedNodeIds.size > 0) {
                    this.marquee.selectedNodeIds.clear();
                    this.marquee.onSelectionChange(this.marquee.selectedNodeIds);
                }
            }
        };
    }

    calculateDepartmentTreeLayout() {
        // 1. STRICT FILTER: Only valid roles matching EXACTLY this department
        const deptRoles = this.data.filter(r => 
            !r.isRoot && 
            r.department === this.activeDepartmentId && 
            r.status !== 'rejected' && 
            r.status !== 'cancelled'
        );
        
        // 2. Deep clone them so we don't accidentally mutate the global tree's children arrays
        let clonedRoles = deptRoles.map(r => ({ 
            ...r, 
            children: [], 
            treeWidth: 0, x: 0, y: 0 
        }));
        
        const deptRoleIds = new Set(clonedRoles.map(r => String(r.selfId)));

        // 3. Create a Virtual Root Node for the Sandbox
        let virtualRoot = {
            selfId: 'virtual-dept-root',
            title: `${this.activeDepartmentId} Group`,
            isRoot: true,
            roleSummary: `Department Hierarchy View`,
            candidateCount: deptRoles.reduce((sum, r) => sum + (r.candidateCount || 0), 0),
            profiles: [],
            children: [],
            treeWidth: 0, x: 0, y: 0,
            displaySettings: { cc: '#F59E0B' }
        };

        const tempNodeMap = { [virtualRoot.selfId]: virtualRoot };
        clonedRoles.forEach(r => tempNodeMap[String(r.selfId)] = r);

        // 4. Build hierarchy STRICTLY within these roles
        clonedRoles.forEach(r => {
            let pId = String(r.parentId);
            
            // If this role's parent is NOT in this specific department, 
            // we attach it directly to the Virtual Root so it doesn't break the chain.
            if (!deptRoleIds.has(pId)) {
                virtualRoot.children.push(r);
                r.parentId = virtualRoot.selfId; 
            } else {
                // If the parent IS in this department, maintain their exact hierarchy
                if (tempNodeMap[pId]) {
                    tempNodeMap[pId].children.push(r);
                }
            }
        });

        this.treeRoot = virtualRoot;
        this.nodeLookup = tempNodeMap; // Isolate lookup table for this view

        this.measureTree(this.treeRoot);
        this.arrangeTree(this.treeRoot, 0, 0);
    }

    draw() {
        this.svgLayer.innerHTML = '';
        
        // ✨ CLEANUP: Ensure the empty text overlay is removed before drawing ANYTHING
        const oldOverlay = document.getElementById('sb-empty-overlay');
        if (oldOverlay) oldOverlay.remove();
        
        const oldNodes = this.canvas.querySelectorAll('.sb-node, .sb-add-btn, .sb-dept-card, .sb-dept-back-btn');
        oldNodes.forEach(n => n.remove());

        if (this.viewMode === 'hierarchy' || (this.viewMode === 'departments' && this.activeDepartmentId)) {
            if(this.treeRoot) this.drawRecursive(this.treeRoot);
            this.drawMinimap();

            if (this.viewMode === 'departments') {
                const backBtn = document.createElement('button');
                backBtn.className = 'sb-dept-back-btn';
                backBtn.onclick = () => { this.activeDepartmentId = null; this.refreshData(); this.resetView(); };
                this.wrapper.appendChild(backBtn); 
            }

        } else if (this.viewMode === 'departments' && !this.activeDepartmentId) {
            this.drawGroupsGrid();
            if (this.minimap) this.minimap.innerHTML = ''; 
        }
    }

    drawGroupsGrid() {
        const cardsPerRow = 4;
        const gap = 40;
        const cardW = 280;
        const cardH = 140;

        // 1. Get departments from Org Config
        const safeDepts = Array.isArray(this.departments) ? this.departments : [];
        let activeDepts = safeDepts.map(d => typeof d === 'object' ? d.label : d).filter(Boolean);
        
        // 2. Add any custom departments that roles are currently using
        this.data.forEach(r => {
            if (!r.isRoot && r.status !== 'rejected' && r.status !== 'cancelled' && r.department) {
                if (!activeDepts.includes(r.department)) activeDepts.push(r.department);
            }
        });

        // 3. SAFE FALLBACK TEXT: Put it in the wrapper so it doesn't get shrunk by the camera!
        let emptyOverlay = document.getElementById('sb-empty-overlay');
        if (activeDepts.length === 0) {
            if (!emptyOverlay) {
                emptyOverlay = document.createElement('div');
                emptyOverlay.id = 'sb-empty-overlay';
                emptyOverlay.style.cssText = "position:absolute; top:40%; left:50%; transform:translate(-50%,-50%); color:#64748b; font-size:16px; font-weight:600; text-align:center; z-index:1000;";
                emptyOverlay.innerHTML = `No Groups/Departments have been configured.<br><span style="font-size:13px; font-weight:400; margin-top:8px; display:block;">Go to Settings > Configuration to add Groups.</span>`;
                this.wrapper.appendChild(emptyOverlay);
            }
            return;
        } else if (emptyOverlay) {
            emptyOverlay.remove();
        }

        let startX = 0, startY = 0;
        
        activeDepts.forEach((deptName, i) => {
            const rolesInDept = this.data.filter(r => 
                !r.isRoot && r.department === deptName && r.status !== 'rejected' && r.status !== 'cancelled'
            );
            
            // 🛑 CRITICAL FIX: We removed the "skip if empty" line!
            // Now, even if a group has 0 roles, it will render nicely on the screen.
            const roleCount = rolesInDept.length;
            const headCount = rolesInDept.reduce((sum, r) => sum + (r.candidateCount || 0), 0);

            const col = i % cardsPerRow;
            const row = Math.floor(i / cardsPerRow);
            const xPos = startX + col * (cardW + gap);
            const yPos = startY + row * (cardH + gap);

            const card = document.createElement('div');
            card.className = 'sb-dept-card';
            card.style.left = `${xPos}px`;
            card.style.top = `${yPos}px`;
            
            card.innerHTML = `
                <div class="sb-dept-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path></svg></div>
                <div class="sb-dept-title">${deptName}</div>
                <div class="sb-dept-sub">${roleCount} Roles • ${headCount} People</div>
            `;

            card.onclick = () => {
                this.activeDepartmentId = deptName;
                this.refreshData();
                this.resetView(); 
            };
            
            this.canvas.appendChild(card);
        });
    }

    setupNotifications() {
        const notifyBtn = this.wrapper.querySelector('#sb-notify-btn');
        const notifyPanel = this.wrapper.querySelector('#sb-notify-panel');

        notifyBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            if (notifyPanel.style.display === 'block') {
                notifyPanel.style.display = 'none';
            } else {
                this.fetchPendingJoins().then(() => this.loadNotifications());
                notifyPanel.style.display = 'block';
            }
        });

        document.addEventListener('click', (e) => {
            if (!notifyBtn.contains(e.target) && !notifyPanel.contains(e.target)) {
                notifyPanel.style.display = 'none';
            }
        });

        this.fetchPendingJoins().then(() => this.refreshNotifications());
        this.pollInterval = setInterval(() => {
            this.fetchPendingJoins().then(() => this.refreshNotifications());
        }, 15000);
    }

    async fetchPendingJoins() {
        try {
            const token = localStorage.getItem('accessToken') || localStorage.getItem('feathers-jwt');
            const res = await fetch(`${this.apiUrl}/api/organization/${this.orgId}/pending-joins`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const json = await res.json();
                this.pendingJoins = json.data || [];
            }
        } catch (err) {
            console.warn("Could not fetch pending joins", err);
            this.pendingJoins = [];
        }
    }

    refreshNotifications() {
        const badge = this.wrapper.querySelector('#sb-notify-badge');
        const panel = this.wrapper.querySelector('#sb-notify-panel');

        const userId = this.currentUser._id;
        let count = 0;

        if (this.canApprove) {
            count += this.data.filter(r => r && r.status === 'pending').length;
            count += this.pendingJoins.length;
        } else {
            count += this.data.filter(r => r && r.requestedBy === userId && r.status === 'rejected').length;
        }

        if (count > 0) {
            badge.innerText = count;
            badge.style.display = 'block';
        } else {
            badge.innerText = '';
            badge.style.display = 'none';
        }

        if (panel && panel.style.display === 'block') {
            this.loadNotifications();
        }

        // 👉 MAKE SURE THIS LINE IS HERE!
        window.dispatchEvent(new CustomEvent('sidebar-sync-notifications', { detail: count }));
    }

    loadNotifications() {
        const panel = this.wrapper.querySelector('#sb-notify-panel');
        panel.innerHTML = '';
        
        const userId = this.currentUser._id;
        let requests = [];

        if (this.canApprove) {
            this.data.forEach(r => { if (r.status === 'pending') requests.push({ type: 'ROLE_CREATE', role: r }); });
            this.pendingJoins.forEach(p => { requests.push({ type: 'ROLE_JOIN', profile: p }); });
        } else {
            this.data.forEach(r => { if (r.requestedBy === userId && r.status === 'rejected') requests.push({ type: 'ROLE_CREATE', role: r }); });
        }

        if (requests.length > 0) {
            requests.forEach(req => {
                const item = document.createElement('div');
                item.className = 'sb-notify-item';

                let titleText = '', statusText = '', msgText = '', statusRaw = '', targetId = '';

                if (req.type === 'ROLE_CREATE') {
                    titleText = req.role.title;
                    statusRaw = req.role.status;
                    statusText = req.role.status;
                    targetId = req.role.selfId;

                    let requesterName = req.role.requestedBy; // Default to ID
                    
                    // 1. Check if it's the current user
                    if (String(this.currentUser._id) === String(req.role.requestedBy)) {
                        requesterName = this.currentUser.name || this.currentUser.firstName || (this.currentUser.userData && this.currentUser.userData.firstName) || this.currentUser.email || requesterName;
                    } 
                    
                    // 2. Search through loaded profiles (in case the name is in the node data)
                    for (const r of this.data) {
                        const profiles = r.profiles || r.kvdrRoleProfiles || [];
                        const user = profiles.find(p => String(p.userId) === String(req.role.requestedBy) || String(p.id) === String(req.role.requestedBy));
                        if (user) {
                            requesterName = user.name || user.firstName || user.email || requesterName;
                            break;
                        }
                    }

                    msgText = `New role requested by: ${requesterName}`;
                } else {
                    titleText = req.profile.candidateName || req.profile.userId;
                    statusRaw = req.profile.status;
                    statusText = 'pending';
                    targetId = req.profile.selfId;
                    msgText = `Requested to join: ${req.profile.roleTitle}`;
                }

                const statusColor = statusRaw === 'rejected' ? '#dc3545' : '#eab308';
                
                item.innerHTML = `
                    <div class="sb-notify-title">
                        ${titleText}
                        <span class="sb-notify-badge" style="background:${statusColor}; color:#fff;">${statusText}</span>
                    </div>
                    <div class="sb-notify-msg">${msgText}</div>
                    ${(this.canApprove && statusRaw !== 'published') ? `
                        <div class="sb-notify-actions">
                            <button class="sb-notify-action-btn sb-notify-approve">✓ Approve</button>
                            <button class="sb-notify-action-btn sb-notify-reject">✗ Reject</button>
                        </div>
                    ` : ''}
                `;

                if (this.canApprove && statusRaw !== 'published') {
                    item.querySelector('.sb-notify-approve').onclick = (e) => {
                        e.stopPropagation();
                        if (req.type === 'ROLE_CREATE') this.handleAction(req.role, 'published');
                        else this.handleJoinAction(req.profile, 'assigned');
                    };
                    item.querySelector('.sb-notify-reject').onclick = (e) => {
                        e.stopPropagation();
                        if (req.type === 'ROLE_CREATE') this.handleAction(req.role, 'rejected');
                        else this.handleJoinAction(req.profile, 'rejected');
                    };
                }

                item.onclick = () => this.highlightAndCenterNode(targetId);
                panel.appendChild(item);
            });
        } else {
            panel.innerHTML = `<div style="padding: 20px; text-align: center; color: #888; font-size: 13px;">No requests available</div>`;
        }
    }

    async handleAction(role, newStatus) {
        if (!this.actions.onUpdate) return;
        try {
            await this.actions.onUpdate(role.selfId, { 
                status: newStatus, 
                analyzedBy: this.currentUser._id 
            });
            
            const target = this.data.find(r => String(r.selfId) === String(role.selfId));
            if (target) target.status = newStatus;

            // ✅ FIX: Removed the `.filter()` deletion here.
            // Keeping it in `this.data` allows the Notification Bell to still see it,
            // while the `calculateTreeLayout` fix ensures it vanishes from the screen instantly!
            
            window.dispatchEvent(new CustomEvent('kvdr-sandbox-refresh', { detail: { graphData: this.data } }));
        } catch (err) {
            console.error('Error updating status:', err);
        }
    }

    async handleJoinAction(profile, newStatus) {
        try {
            const token = localStorage.getItem('accessToken') || localStorage.getItem('feathers-jwt');
            if (newStatus === 'assigned') {
                const payload = {
                    user_id: profile.userId, email: profile.email || "", role_id: profile.selfId,
                    organization_id: this.orgId, api_token: token, score: profile.score || 85,
                    reasoning: profile.reasoning || "Self-Requested", fitment_data: [], role_name: profile.roleTitle
                };
                await fetch(`${this.apiUrl}/api/talent-pool/manual-assign`, {
                    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload)
                });
            } else {
                const payload = {
                    user_id: profile.userId, email: profile.email || "", role_id: profile.selfId,
                    organization_id: this.orgId, role_name: profile.roleTitle, send_email: true
                };
                await fetch(`${this.apiUrl}/api/talent-pool/reject-suggestion`, {
                    method: "POST", headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(payload)
                });
            }

            this.pendingJoins = this.pendingJoins.filter(p => p._id !== profile._id);
            window.dispatchEvent(new CustomEvent('kvdr-sandbox-refresh', { detail: { graphData: this.data } }));
        } catch (err) {
            console.error('Error updating join status:', err);
        }
    }

    calculateTreeLayout() {
        this.cardWidth = 340; 
        this.cardHeight = 180; 
        this.gapX = 80;
        this.gapY = 160;

        const nodeMap = {};
        this.data.forEach(n => {
            // ✨ STRICT FILTER: Ignore rejected or cancelled roles
            if (n.status === 'rejected' || n.status === 'cancelled') return;

            const sId = String(n.selfId);
            let pId = n.parentId ? String(n.parentId) : "0";
            if (pId === "null" || pId === "undefined" || pId === "") pId = "0";
            nodeMap[sId] = { ...n, selfId: sId, parentId: pId, children: [], treeWidth: 0, x: 0, y: 0 };
        });

        const allNodes = Object.values(nodeMap);
        if (allNodes.length === 0) return;

        let root = nodeMap[String(this.rootId)];
        if (!root) root = allNodes.find(n => n.parentId === "0" || n.parentId === "null");
        if (!root) root = allNodes[0];

        allNodes.forEach(n => {
            if (n.selfId === root.selfId) return;
            let pId = n.parentId;
            if (pId === "0" || !nodeMap[pId]) pId = root.selfId; 
            if (nodeMap[pId]) nodeMap[pId].children.push(n);
        });

        this.treeRoot = root;
        this.nodeLookup = nodeMap;

        this.measureTree(this.treeRoot);
        this.arrangeTree(this.treeRoot, 0, 0);
    }

    measureTree(node) {
        if (node.children.length === 0) {
            node.treeWidth = this.cardWidth + this.gapX;
            return node.treeWidth;
        }
        let w = 0;
        node.children.forEach(c => { w += this.measureTree(c); });
        node.treeWidth = w;
        return w;
    }

    arrangeTree(node, startX, level) {
        node.y = level * (this.cardHeight + this.gapY);
        if (node.children.length === 0) {
            node.x = startX + (this.gapX / 2);
            return;
        }
        let currentX = startX;
        node.children.forEach(c => {
            this.arrangeTree(c, currentX, level + 1);
            currentX += c.treeWidth;
        });

        const first = node.children[0];
        const last = node.children[node.children.length - 1];
        node.x = ((first.x + last.x) / 2);
    }

    drawRecursive(node) {
        this.renderNodeCard(node);
        node.children.forEach(child => {
            this.drawLink(node, child);
            this.drawRecursive(child);
        });
    }

    renderNodeCard(node) {
        const el = document.createElement('div');
        el.className = 'sb-node';
        el.dataset.id = node.selfId;
        el.style.left = `${node.x}px`;
        el.style.top = `${node.y}px`;
        
        // --- NEW: PENDING CHECK ---
        const isPending = node.status === 'pending';
        if (isPending) {
            el.classList.add('sb-pending-role');
            el.draggable = false;
        } else {
            el.draggable = true;
            el.addEventListener("dragstart", (e) => {
                e.stopPropagation();
                e.dataTransfer.setData("text/plain", node.selfId);
                window.draggedSandboxNodeId = node.selfId;
                el.classList.add('is-dragging');
            });
            el.addEventListener("dragend", (e) => {
                e.stopPropagation();
                el.classList.remove('is-dragging');
                delete window.draggedSandboxNodeId;
                document.querySelectorAll('.drag-over-active').forEach(elem => elem.classList.remove('drag-over-active'));
            });
        }

        el.addEventListener("dragover", (e) => {
            if (isPending) return;
            e.preventDefault(); e.stopPropagation();
            if (window.draggedSandboxNodeId && window.draggedSandboxNodeId !== node.selfId) el.classList.add('drag-over-active');
        });
        el.addEventListener("dragleave", (e) => {
            if (isPending) return;
            e.preventDefault(); e.stopPropagation();
            el.classList.remove('drag-over-active');
        });
        el.addEventListener("drop", (e) => {
            if (isPending) return;
            e.preventDefault(); e.stopPropagation();
            el.classList.remove('drag-over-active');
            const draggedId = e.dataTransfer.getData("text/plain");
            if(draggedId && draggedId !== node.selfId) {
                const draggedNode = this.data.find(n => n.selfId === draggedId);
                const draggedName = draggedNode ? draggedNode.title : "this role";
                
                RolePopupManager.showConfirmMovePopup(draggedName, node.title, () => {
                    this.actions.onUpdate && this.actions.onUpdate(draggedId, { parentId: node.selfId });
                    const targetLocal = this.data.find(n => n.selfId === draggedId);
                    if(targetLocal) targetLocal.parentId = node.selfId;
                    window.dispatchEvent(new CustomEvent('kvdr-sandbox-refresh', { detail: { graphData: this.data } }));
                });
            }
        });

        const svgTrash = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>`;
        const svgEdit = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>`;
        const svgUser = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>`;
        const svgLock = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>`;
        const svgUnlock = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 9.9-1"></path></svg>`;

        const isRootNode = String(node.selfId) === String(this.rootId) || node.isRoot;
        
        const rawProfiles = node.profiles || node.kvdrRoleProfiles || [];
        const profiles = rawProfiles.filter(p => {
            const isInactive = p.isActive === false || p.isDeleted === true || 
                            (p.status && ['inactive', 'deleted', 'removed', 'archived', 'deactivated'].includes(String(p.status).toLowerCase())) ||
                            (p.userData && (p.userData.isActive === false || p.userData.isDeleted === true || 
                            (p.userData.status && ['inactive', 'deleted', 'removed', 'archived', 'deactivated'].includes(String(p.userData.status).toLowerCase()))));
            return !isInactive;
        });

        let displayCount = node.candidateCount || 0;
        if (!isRootNode && rawProfiles.length > 0) {
            displayCount = profiles.length; 
        }
        
        const handleEditSuccess = async (updatedData) => {
            try {
                let result = null;
                if (this.actions.onUpdate) {
                    result = await this.actions.onUpdate(node.selfId, updatedData);
                }
                const targetNode = this.data.find(n => n.selfId === node.selfId);
                if (targetNode) Object.assign(targetNode, updatedData);
                window.dispatchEvent(new CustomEvent('kvdr-sandbox-refresh', { detail: { graphData: this.data } }));
                return result; 
            } catch (error) {
                console.error("Failed to update and refresh the role:", error);
            }
        };

        const headerColor = node.displaySettings?.cc || this.displaySettings?.cc || '#48A9C5';

        if (isRootNode) {
            el.classList.add('sb-root-node-card');
            el.style.backgroundColor = headerColor; 
            
            const orgLogoUrl = node.image || node.logo || node.logoUrl || (node.data && node.data.image) || null;
            let logoHTML = '';

            if (orgLogoUrl && orgLogoUrl !== 'null' && orgLogoUrl !== '') {
                const finalLogoSrc = orgLogoUrl.startsWith('http') ? orgLogoUrl : `https://gtbrdd-storage.s3.us-west-2.amazonaws.com/editor/profile-pictures/${orgLogoUrl}`;
                logoHTML = `<img src="${finalLogoSrc}" class="sb-root-logo-img" alt="Org Logo" onerror="this.parentElement.innerHTML='<div class=sb-root-logo-fallback>!</div>'" />`;
            } else {
                logoHTML = `<div class="sb-root-logo-fallback"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="4" width="16" height="16" rx="2" ry="2"></rect><rect x="9" y="9" width="6" height="6"></rect><line x1="9" y1="1" x2="9" y2="4"></line><line x1="15" y1="1" x2="15" y2="4"></line><line x1="9" y1="20" x2="9" y2="23"></line><line x1="15" y1="20" x2="15" y2="23"></line><line x1="20" y1="9" x2="23" y2="9"></line><line x1="20" y1="14" x2="23" y2="14"></line><line x1="1" y1="9" x2="4" y2="9"></line><line x1="1" y1="14" x2="4" y2="14"></line></svg></div>`;
            }

            el.innerHTML = `
                <div class="sb-root-content">
                    <div class="sb-root-title" title="${node.title}">${node.title || "Organization Root"}</div>
                    ${logoHTML}
                </div>
                <div style="margin-top: auto; margin-bottom: 24px;">
                    <div class="sb-badge" style="background: rgba(255,255,255,0.9); color: #0f172a; border: none; font-size: 13px; padding: 6px 14px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                        ${svgUser} <b>${displayCount} Total Headcount</b>
                    </div>
                </div>
            `;
            
            el.addEventListener('click', (e) => {
                e.stopPropagation();
                this.showRootNodePopup();
            });

        } else {
            let summaryText = node.roleSummary || '';
            if(!summaryText && Array.isArray(node.description) && node.description.length > 0) {
                const lastEntry = [...node.description].reverse().find(d => d && typeof d.msg === 'string');
                if(lastEntry) summaryText = lastEntry.msg.replace('[ROLE_DESC_SAVE]','');
            }

            // --- NEW: Strip HTML tags to ensure clean plain text for the summary card ---
            if (summaryText) {
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = summaryText;
                summaryText = tempDiv.textContent || tempDiv.innerText || '';
                summaryText = summaryText.trim();
            }
            // --------------------------------------------------------------------------

            let avatarsHTML = '';
            if (profiles.length > 0) {
                const maxDisplay = 4;
                const displayProfiles = profiles.slice(0, maxDisplay);
                const remaining = profiles.length - maxDisplay;

                avatarsHTML = `<div class="sb-avatars-row">`;
                displayProfiles.forEach((p, i) => {
                    const uid = p.userId || p.user_id || p._id || p.id || `unknown-${i}`;
                    const ud = p.userData || {};
                    const first = p.firstName || p.first_name || ud.firstName || ud.first_name || '';
                    const rawName = p.name || p.fullName || p.display_name || ud.name || ud.fullName || '';
                    const finalHoverName = (first || rawName || "User Profile").trim();

                    let imgUrl = '';
                    const picUrl = ud.profilePictureUrl || p.profilePictureUrl || ud.profilePicture || p.profilePicture || p.avatar;

                    if (picUrl && typeof picUrl === 'string' && picUrl !== "null" && picUrl.length > 3) {
                        imgUrl = picUrl.startsWith('http') ? picUrl : `https://gtbrdd-storage.s3.us-west-2.amazonaws.com/editor/profile-pictures/${picUrl}`;
                    } else {
                        imgUrl = `https://hostedboringavatars.vercel.app/api/beam?name=${encodeURIComponent(finalHoverName)}`;
                    }

                    avatarsHTML += `<img src="${imgUrl}" class="sb-avatar" style="z-index: ${maxDisplay - i};" data-tooltip="${finalHoverName}" data-uid="${uid}" onerror="this.src='https://hostedboringavatars.vercel.app/api/beam?name=Error'" />`;
                });
                
                if (remaining > 0) {
                    avatarsHTML += `<div class="sb-avatar-more" data-tooltip="${remaining} more" style="z-index: 0;">+${remaining}</div>`;
                }
                avatarsHTML += `</div>`;
            } else {
                avatarsHTML = `<div class="sb-avatars-row"><span style="font-size:11px; color:#94a3b8; font-style:italic;">Unassigned</span></div>`;
            }

            const badgeHTML = `<div class="sb-badge" style="background:#e0f2fe; color:#0284c7;">${svgUser} ${displayCount}</div>`;

            const LOCK_FLAG = "[OPEN_HIRING]";
            let currentSoftSkills = Array.isArray(node.softSkills) ? [...node.softSkills] : [];
            let isHiring = currentSoftSkills.includes(LOCK_FLAG);

            // Add Badge if pending
            const pendingBadgeHtml = isPending ? `<span style="font-size:10px; background:#fef08a; color:#854d0e; padding:2px 8px; border-radius:12px; font-weight:700; margin-left:8px; border:1px solid #eab308;">Pending</span>` : '';

            el.innerHTML = `
                <div class="sb-node-header" style="background-color: ${headerColor};">
                    <span class="sb-title-span" title="${node.title}">${node.title}</span>
                    ${pendingBadgeHtml}
                    <div class="sb-header-actions" style="${isPending ? 'display:none;' : ''}">
                        <button class="sb-act-btn lock-btn" style="color: ${isHiring ? '#28c76f' : '#f44336'};">${isHiring ? svgUnlock : svgLock}</button>
                        <button class="sb-act-btn edit-btn" data-tooltip="Edit Role">${svgEdit}</button>
                        <button class="sb-act-btn del-btn" data-tooltip="Delete">${svgTrash}</button>
                    </div>
                </div>
                <div class="sb-node-body" title="${isPending ? 'Action restricted while pending' : 'Click to view details'}">
                    <div class="sb-desc-box">${summaryText || "No description provided."}</div>
                    <div class="sb-footer-row">
                        ${avatarsHTML}
                        <div class="sb-stats">${badgeHTML}</div>
                    </div>
                </div>
            `;

            const nodeBody = el.querySelector('.sb-node-body');
            if (nodeBody && !isPending) {
                nodeBody.addEventListener('click', (e) => {
                    e.stopPropagation();
                    if (e.target.classList.contains('sb-avatar')) {
                        const uid = e.target.dataset.uid;
                        window.dispatchEvent(new CustomEvent('show-talent-card', { detail: { userId: uid } }));
                        if (this.actions.onProfileClick) this.actions.onProfileClick(node.selfId, uid);
                    } else {
                        window.dispatchEvent(new CustomEvent('show-role-details', { detail: { role: node } }));
                    }
                });
            }

            const lockBtn = el.querySelector('.lock-btn');
            if (lockBtn && !isPending) {
                lockBtn.addEventListener('mouseenter', () => {
                    const tip = document.createElement('div');
                    tip.className = 'sandbox-custom-tooltip lock-tip';
                    tip.innerText = isHiring ? 'Role is Open' : 'Role is Closed';
                    document.body.appendChild(tip);
                    const rect = lockBtn.getBoundingClientRect();
                    tip.style.top = (rect.top - tip.offsetHeight - 5) + 'px';
                    tip.style.left = (rect.left + (rect.width / 2)) + 'px';
                });
                lockBtn.addEventListener('mouseleave', () => {
                    document.querySelectorAll('.lock-tip').forEach(t => t.remove());
                });

                let isTogglingLock = false;
                lockBtn.onclick = async (e) => {
                    e.stopPropagation();
                    if (isTogglingLock) return;
                    isTogglingLock = true;

                    document.querySelectorAll('.lock-tip').forEach(t => t.remove());
                    
                    isHiring = !isHiring;
                    lockBtn.innerHTML = isHiring ? svgUnlock : svgLock;
                    lockBtn.style.color = isHiring ? '#28c76f' : '#f44336';

                    if (isHiring) {
                        if (!currentSoftSkills.includes(LOCK_FLAG)) currentSoftSkills.push(LOCK_FLAG);
                    } else {
                        currentSoftSkills = currentSoftSkills.filter(skill => skill !== LOCK_FLAG);
                    }
                    
                    node.softSkills = currentSoftSkills;

                    try {
                        if (this.actions.onUpdate) {
                            await this.actions.onUpdate(node.selfId, { softSkills: currentSoftSkills });
                            const targetNode = this.data.find(n => n.selfId === node.selfId);
                            if (targetNode) targetNode.softSkills = currentSoftSkills;
                        }
                    } catch (err) {
                        console.error("Failed to toggle lock:", err);
                        isHiring = !isHiring;
                        lockBtn.innerHTML = isHiring ? svgUnlock : svgLock;
                        lockBtn.style.color = isHiring ? '#28c76f' : '#f44336';
                    } finally {
                        setTimeout(() => { isTogglingLock = false; }, 500);
                    }
                };
            }

            const editBtn = el.querySelector('.edit-btn');
            if (editBtn && !isPending) editBtn.onclick = (e) => { 
                e.stopPropagation(); 
                window.dispatchEvent(new CustomEvent('open-role-editor', { 
                    detail: { mode: 'EDIT', role: node, onSave: handleEditSuccess } 
                })); 
            };

            const delBtn = el.querySelector('.del-btn');
            if (delBtn && !isPending) delBtn.onclick = (e) => { 
                e.stopPropagation();
                let subRoles = this.data.filter(n => n.parentId === node.selfId).length;
                RolePopupManager.showDeleteRolePopup(node, subRoles, displayCount, () => {
                    if (this.actions.onDelete) this.actions.onDelete(node.selfId); 
                });
            };
        }

        // --- NEW: Add Role Icon ONLY if NOT pending ---
        if (!isPending) {
            const addBtn = document.createElement('div');
            addBtn.className = 'sb-add-btn';
            addBtn.dataset.nodeId = node.selfId; 
            addBtn.innerHTML = `<span>+</span>`;
            addBtn.style.left = `${node.x + (this.cardWidth / 2)}px`;
            addBtn.style.top = `${node.y + this.cardHeight}px`;
            
            addBtn.onclick = (e) => { 
                e.stopPropagation(); 
                window.dispatchEvent(new CustomEvent('open-role-editor', { 
                    detail: { 
                        mode: 'CREATE', 
                        role: { title: "" }, 
                        onSave: async (newData) => { 
                            if(this.actions.onAdd) {
                                return await this.actions.onAdd(node.selfId, newData);
                            }
                        }
                    } 
                }));
            };
            this.canvas.appendChild(addBtn);
        }

        this.canvas.appendChild(el);
        el.querySelectorAll('[data-tooltip]').forEach(elem => {
            this.attachTooltip(elem, elem.getAttribute('data-tooltip'));
        });
    }

    drawLink(p, c) {
        const pX = p.x + (this.cardWidth / 2);
        const pY = p.y + this.cardHeight;
        const cX = c.x + (this.cardWidth / 2);
        const cY = c.y;
        const midY = pY + (cY - pY) / 2;
        const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
        path.setAttribute("d", `M ${pX} ${pY} L ${pX} ${midY} L ${cX} ${midY} L ${cX} ${cY}`);
        path.setAttribute("class", "sb-connector");
        this.svgLayer.appendChild(path);
    }

    drawMinimap() {
       // Code truncated for length (same as your original drawMinimap implementation)
       if(!this.treeRoot || !this.minimap) return;
        this.minimap.innerHTML = '';
        
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        const traverse = (n) => {
            if(n.x < minX) minX = n.x;
            if(n.x + this.cardWidth > maxX) maxX = n.x + this.cardWidth;
            if(n.y < minY) minY = n.y;
            
            const addBtnAndMargin = 30;
            if(n.y + this.cardHeight + addBtnAndMargin > maxY) maxY = n.y + this.cardHeight + addBtnAndMargin;

            n.children.forEach(traverse);
        };
        traverse(this.treeRoot);
        
        minX -= 300; maxX += 300; minY -= 300; maxY += 300;
        const treeW = maxX - minX;
        const treeH = maxY - minY;
        const scale = Math.min(200 / treeW, 150 / treeH);
        
        const drawMini = (n) => {
            const d = document.createElement('div');
            d.className = 'sb-mini-node';
            d.style.left = `${(n.x - minX) * scale}px`;
            d.style.top = `${(n.y - minY) * scale}px`;
            d.style.width = `${this.cardWidth * scale}px`;
            d.style.height = `${this.cardHeight * scale}px`;
            this.minimap.appendChild(d);
            n.children.forEach(drawMini);
        };
        drawMini(this.treeRoot);
        
        this.miniViewport = document.createElement('div');
        this.miniViewport.className = 'sb-mini-viewport';
        this.minimap.appendChild(this.miniViewport);
        this.mapState = { minX, minY, scale };
        this.updateMinimapViewport();
    }

    updateMinimapViewport() {
        if(!this.miniViewport || !this.mapState) return;
        const screenW = this.wrapper.clientWidth;
        const screenH = this.wrapper.clientHeight;
        const worldLeft = -this.panX / this.zoom;
        const worldTop = -this.panY / this.zoom;
        
        this.miniViewport.style.left = `${(worldLeft - this.mapState.minX) * this.mapState.scale}px`;
        this.miniViewport.style.top = `${(worldTop - this.mapState.minY) * this.mapState.scale}px`;
        this.miniViewport.style.width = `${(screenW / this.zoom) * this.mapState.scale}px`;
        this.miniViewport.style.height = `${(screenH / this.zoom) * this.mapState.scale}px`;
    }

    handleZoom(zoomMultiplier, originX, originY) {
        const newZoom = Math.min(Math.max(0.15, this.zoom * zoomMultiplier), 3);
        if (newZoom === this.zoom) return;
        const scaleRatio = newZoom / this.zoom;
        this.panX = originX - (originX - this.panX) * scaleRatio;
        this.panY = originY - (originY - this.panY) * scaleRatio;
        this.zoom = newZoom;
        this.updateTransform();
    }

    triggerSmoothZoom(multiplier) {
        if (!this.wrapper) return;
        const rect = this.wrapper.getBoundingClientRect();
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        this.canvas.style.transition = "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)";
        this.handleZoom(multiplier, centerX, centerY);
        clearTimeout(this.zoomTransitionTimeout);
        this.zoomTransitionTimeout = setTimeout(() => {
            if (this.canvas) this.canvas.style.transition = "none";
        }, 250);
    }

    resetView() {
        if (!this.wrapper || this.wrapper.clientWidth === 0) return;

        const wrapperW = this.wrapper.clientWidth;
        const wrapperH = this.wrapper.clientHeight;

        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;

        if (this.viewMode === 'departments' && !this.activeDepartmentId) {
            const cards = this.canvas.querySelectorAll('.sb-dept-card');
            
            // 🛑 FIX: If no cards exist, just center the camera normally and stop.
            if (cards.length === 0) {
                this.zoom = 1;
                this.panX = 0;
                this.panY = 0;
                this.updateTransform();
                return;
            }
            
            cards.forEach(card => {
                const x = parseFloat(card.style.left);
                const y = parseFloat(card.style.top);
                const w = 280; const h = 140; 
                if (x < minX) minX = x; if (x + w > maxX) maxX = x + w;
                if (y < minY) minY = y; if (y + h > maxY) maxY = y + h;
            });
        } else {
            if (!this.treeRoot) return;
            const traverse = (n) => {
                if (n.x < minX) minX = n.x; if (n.x + this.cardWidth > maxX) maxX = n.x + this.cardWidth;
                if (n.y < minY) minY = n.y;
                const addBtnH = 40; 
                if (n.y + this.cardHeight + addBtnH > maxY) maxY = n.y + this.cardHeight + addBtnH;
                n.children.forEach(traverse);
            };
            traverse(this.treeRoot);
        }

        if (minX === Infinity) return;

        const contentWidth = maxX - minX;
        const contentHeight = maxY - minY;
        
        const PADDING_X = wrapperW * 0.1; 
        const PADDING_Y = wrapperH * 0.1; 

        const zoomX = wrapperW / (contentWidth + PADDING_X * 2);
        const zoomY = wrapperH / (contentHeight + PADDING_Y * 2);
        
        this.zoom = Math.min(Math.max(Math.min(zoomX, zoomY), 0.3), 1.0);

        const centerX = minX + contentWidth / 2;
        const centerY = minY + contentHeight / 2;

        this.panX = (wrapperW / 2) - (centerX * this.zoom);
        
        if ((contentHeight * this.zoom) < wrapperH * 0.8) {
            this.panY = (wrapperH * 0.25) - (minY * this.zoom); 
        } else {
            this.panY = (wrapperH / 2) - (centerY * this.zoom);
        }

        this.updateTransform();
    }

    updateTransform() {
        this.canvas.style.transform = `translate(${this.panX}px, ${this.panY}px) scale(${this.zoom})`;
        this.updateMinimapViewport();
    }

    attachEvents() {
        // Keep your existing touch/mouse dragging events
        let startX=0, startY=0;

        this.wrapper.addEventListener('mousedown', (e) => {
            if(e.target.closest('.sb-node') || e.target.closest('button') || e.target.closest('.sb-add-btn') || e.target.closest('.sb-search-wrapper') || e.target.closest('.sb-notify-panel')) return;

            if (this.activeTool === 'select' || e.shiftKey) {
                this.marquee.startSelection(e);
            } else {
                // Clear active selection when clicking the background to pan
                if (this.marquee && this.marquee.selectedNodeIds.size > 0) {
                    this.marquee.selectedNodeIds.clear();
                    this.marquee.onSelectionChange(this.marquee.selectedNodeIds);
                }
                
                this.isBackgroundDragging = true;
                startX = e.clientX - this.panX;
                startY = e.clientY - this.panY;
                this.wrapper.style.cursor = 'grabbing';
            }
        });
        
        window.addEventListener('mousemove', (e) => {
            if(this.isBackgroundDragging) {
                e.preventDefault();
                this.panX = e.clientX - startX;
                this.panY = e.clientY - startY;
                this.updateTransform();
            }
        });
        
        window.addEventListener('mouseup', () => { 
            if (this.isBackgroundDragging) {
                this.isBackgroundDragging = false; 
                this.wrapper.style.cursor = 'grab';
            }
        });
        
        this.wrapper.addEventListener('wheel', (e) => {
            if(e.target.closest('.sb-notify-panel') || e.target.closest('.sb-search-dropdown')) return;
            e.preventDefault();
            const zoomSensitivity = 0.002; 
            let delta = -e.deltaY * zoomSensitivity;
            if (e.deltaMode === 1) delta *= 15;
            const zoomMultiplier = Math.exp(delta);
            const rect = this.wrapper.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;
            this.handleZoom(zoomMultiplier, mouseX, mouseY);
        }, { passive: false });

        if (this.resizeObserver) this.resizeObserver.disconnect();
        this.resizeObserver = new ResizeObserver(() => {
            clearTimeout(this.resizeTimeout);
            this.resizeTimeout = setTimeout(() => this.resetView(), 100);
        });
        this.resizeObserver.observe(this.wrapper);
    }

    _updateNodeSelectionStyles(selectedIds) {
        const allNodeElements = this.wrapper.querySelectorAll('.sb-node');
        allNodeElements.forEach(nodeEl => {
            if (selectedIds.has(nodeEl.dataset.id)) {
                nodeEl.classList.add('is-selected');
            } else {
                nodeEl.classList.remove('is-selected');
            }
        });
    }
}


